import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getPreviousQuestionsValue, isValid } from 'src/common/utils/functions';
import { DEFAULT_ERROR_STATE } from 'src/containers/client-assessment/types';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import ErrorText from 'src/common/components/ErrorScreen/ErrorText';
import { ChildCommonProps } from './types';
import { mobileBreakpoint } from 'src/containers/client-assessment/mobileBreakpoint';

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 7em;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  width: 13em;
  margin-bottom: 8px;
  @media ${mobileBreakpoint.mobile} {
    width: 100%;
  }
`;

const Input = styled.input`
  width: 100%;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 48px;
  font-weight: 400;
  color: #101828;
  border: 1px solid ${props => props.theme.grey.regular};

  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  &:focus-visible {
    outline: none;
  }
`;

const IndicatorsWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 35px;
  right: 0;
  height: 100%;
`;

type IndicatorProps = {
  bottom?: boolean;
};

const Indicator = styled.div`
  padding: 9px 8px;
  border: 1px solid ${props => props.theme.grey.regular};
  height: 50%;
  svg {
    color: ${props => props.theme.main.black};
    font-size: 18px;
  }
  &:hover {
    cursor: pointer;
  }
  ${(props: IndicatorProps) =>
    props.bottom
      ? 'border-bottom-right-radius: 6px; border-top: none; border-bottom: none;'
      : ' border-top-right-radius: 6px;'}
`;

const errorCheck = (
  value: any,
  attributeKey: string,
  extraData: any,
  minValue?: any,
  maxValue?: any,
) => {
  if ([NaN, undefined, null].includes(value))
    return { message: "Input can't be blank", valid: true };
  if (minValue !== 0 && value === 0)
    return { message: "Input can't be zero", valid: true };
  else {
    const isValueLessThanMin = minValue ? value < minValue : false;
    const isValueGreaterThanMax = maxValue ? value > maxValue : false;

    const isInvalidPurchaseDate = () => {
      if (typeof minValue === 'number') {
        const currentYear = new Date()?.getFullYear() ?? 2023;
        return value < +minValue || value > currentYear;
      }
      return false;
    };
    switch (attributeKey) {
      case 'spouse_age':
      case 'spouse_retirement_age':
      case 'retirement_age':
      case 'net_worth_age':
      case 'age':
        if (value < 18)
          return { message: "Age can't be less than 18", valid: true };
        if (value > 120)
          return { message: "Age can't exceed 120", valid: true };
        return DEFAULT_ERROR_STATE;
      case 'household_body_count':
        if (value < 1)
          return { message: 'Minimum value needs to be 1', valid: true };
        if (value > 50)
          return { message: "Value can't exceed 50", valid: true };
        return DEFAULT_ERROR_STATE;
      case 'household_body_count_under_18':
        if (extraData <= value)
          return {
            message:
              "Dependents under 18 can't be more than actual family members",
            valid: true,
          };
        if (value < 0)
          return { message: "Value can't be negative", valid: true };
        if (value > 50)
          return { message: "Value can't exceed 50", valid: true };
        return DEFAULT_ERROR_STATE;
      case 'geo_zip_code':
        if (value.toString().length !== 5)
          return { message: 'Zip code should be of 5 digits', valid: true };
        return DEFAULT_ERROR_STATE;
      case 'house_purchase_date':
        if (isInvalidPurchaseDate())
          return {
            message: `Value should be between ${minValue} and ${maxValue}`,
            valid: true,
          };
      case 'cur_high_int_debt_bal':
        if (isValueLessThanMin || isValueGreaterThanMax)
          return {
            message: `Value must be between ${minValue} and ${maxValue ?? '-'}`,
            valid: true,
          };
      default:
        return DEFAULT_ERROR_STATE;
    }
  }
};

type Props = ChildCommonProps;
function NumberInput({
  label,
  setQuestionnaireValue,
  questionnaireValue,
  selectedQuestionId,
  type = 'text',
  attributeKey,
  metadata,
  error,
  setError,
  questions,
}: Props) {
  const [value, setValue] = useState<any>(0);
  useEffect(() => {
    const currentQuestionValue = questionnaireValue[selectedQuestionId];
    if (isValid(currentQuestionValue)) {
      setValue(currentQuestionValue);
    } else if (attributeKey === 'spouse_age') {
      const getSpouseAge = getPreviousQuestionsValue(
        'age',
        questions ?? [],
        questionnaireValue,
      );
      setValue(getSpouseAge);
    } else if (metadata?.default) {
      setValue(metadata?.default);
    } else {
      setValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeKey]);
  useEffect(() => {
    if (attributeKey === 'household_body_count_under_18') {
      const householdBodyCount = getPreviousQuestionsValue(
        'household_body_count',
        questions ?? [],
        questionnaireValue,
      );
      const check = errorCheck(
        value,
        attributeKey,
        householdBodyCount,
        metadata?.min ?? 0,
        metadata?.max ?? 0,
      );
      setError(check);
    } else {
      const check = errorCheck(
        value,
        attributeKey,
        label,
        metadata?.min ?? 0,
        metadata?.max ?? 0,
      );

      setError(check);
    }

    /* When Household body count is reduced to 0 or 1, household body count under 18 must be reset to 0 */
    if (
      attributeKey === 'household_body_count' &&
      value <= 1 &&
      Array.isArray(questions)
    ) {
      const householdBodyCountUnder18 = questions.find(
        ({ attributeKey }) => attributeKey === 'household_body_count_under_18',
      );
      setQuestionnaireValue({
        ...questionnaireValue,
        [selectedQuestionId]: value,
        ...(householdBodyCountUnder18 && {
          [householdBodyCountUnder18?.questionId]: 0,
        }),
      });
    } else {
      setQuestionnaireValue({
        ...questionnaireValue,
        [selectedQuestionId]:
          attributeKey === 'investment_amount_decline' ? -value : value,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, attributeKey]);

  const handleChangeIndicator = (type: 'INCREASE' | 'DECREASE') => {
    if (![NaN, undefined, null, ''].includes(value))
      setValue(
        type === 'INCREASE' ? +value + 1 : value - 1 > -1 ? +value - 1 : +value,
      );
    else setValue(type === 'INCREASE' ? 1 : -1);
  };
  const { min, max, default: defaultValue } = metadata;
  return (
    <MainWrapper>
      <Wrapper>
        <Input
          id={label + type}
          name={label}
          value={value}
          min={min}
          max={max}
          defaultValue={defaultValue ?? 0}
          onChange={({ target }) =>
            setValue(type === 'number' ? +target.value : target.value)
          }
          type={attributeKey === 'age' ? 'text' : type}
        />
        <IndicatorsWrapper>
          <Indicator onClick={() => handleChangeIndicator('INCREASE')}>
            <FiChevronUp />
          </Indicator>
          <Indicator bottom onClick={() => handleChangeIndicator('DECREASE')}>
            <FiChevronDown />
          </Indicator>
        </IndicatorsWrapper>
      </Wrapper>
      {error?.valid && <ErrorText>{error.message}</ErrorText>}
    </MainWrapper>
  );
}

export default NumberInput;
